import {
  Box, Button, Typography, Divider,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { useSelector } from 'react-redux';
import { TopBar } from 'components';

const formatTime = (ts) => {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  } catch (e) {
    return '';
  }
};

// Formata segundos como "45s" ou "12m 03s".
const formatSeconds = (total) => {
  const s = Math.max(0, Math.round(total || 0));
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m === 0) return `${rem}s`;
  return `${m}m ${rem < 10 ? '0' : ''}${rem}s`;
};

function TeamColumn({ team, isWinner, align }) {
  return (
    <Box sx={{ flex: 1, textAlign: align }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        {team?.name}
        {team?.points !== undefined ? ` (${team.points})` : ''}
        {isWinner ? ' 🏆' : ''}
      </Typography>
      {(team?.players || []).map((p) => (
        <Typography key={p.playerName} variant="body2" sx={{ color: 'text.secondary' }}>
          {p.playerName}
          {p.gender === 'F' ? ' ♀' : ''}
        </Typography>
      ))}
    </Box>
  );
}

function MatchCard({ match }) {
  const winnerA = match.winner === 'A';
  return (
    <Box
      sx={{
        width: '100%',
        bgcolor: 'rgba(255, 255, 255, 0.55)',
        border: '1px solid rgba(0, 0, 0, 0.06)',
        borderRadius: 3,
        p: 2,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary', display: 'block', textAlign: 'center', mb: 1,
        }}
      >
        {formatTime(match.playedAt)}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TeamColumn team={match.teamA} isWinner={winnerA} align="left" />
        <Box sx={{ textAlign: 'center', px: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
            {match.scoreA}
            {' x '}
            {match.scoreB}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>vs</Typography>
        </Box>
        <TeamColumn team={match.teamB} isWinner={!winnerA} align="right" />
      </Box>
      <Divider sx={{ my: 1.5 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {'⏱️ Duração: '}
          <strong>{formatSeconds(match.durationSeconds)}</strong>
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {'🔥 Maior rally: '}
          <strong>{formatSeconds(match.maxRallySeconds)}</strong>
        </Typography>
      </Box>
    </Box>
  );
}

const escapeHtml = (s) => String(s ?? '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));

// Gera o histórico como HTML imprimível e abre a impressão (o usuário salva como PDF).
const exportHistoryPdf = (history) => {
  const dateStr = new Date().toLocaleDateString('pt-BR');
  const teamHtml = (team, isWinner, align) => `
    <div style="flex:1;text-align:${align}">
      <div style="font-weight:700">${escapeHtml(team.name)} (${team.points})${isWinner ? ' 🏆' : ''}</div>
      ${(team.players || []).map((p) => `<div style="color:#555;font-size:13px">${escapeHtml(p.playerName)}${p.gender === 'F' ? ' ♀' : ''}</div>`).join('')}
    </div>`;
  const matchHtml = (m) => `
    <div style="border:1px solid #ddd;border-radius:10px;padding:12px;margin-bottom:12px;page-break-inside:avoid">
      <div style="text-align:center;color:#777;font-size:12px;margin-bottom:6px">${formatTime(m.playedAt)}</div>
      <div style="display:flex;align-items:center;gap:8px">
        ${teamHtml(m.teamA, m.winner === 'A', 'left')}
        <div style="text-align:center;min-width:84px;font-weight:700;font-size:20px">${m.scoreA} x ${m.scoreB}</div>
        ${teamHtml(m.teamB, m.winner === 'B', 'right')}
      </div>
      <div style="border-top:1px solid #eee;margin:10px 0 6px"></div>
      <div style="display:flex;justify-content:space-around;color:#555;font-size:12px">
        <span>Duração: <b>${formatSeconds(m.durationSeconds)}</b></span>
        <span>Maior rally: <b>${formatSeconds(m.maxRallySeconds)}</b></span>
      </div>
    </div>`;

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Histórico ${dateStr}</title></head>
    <body style="font-family:Arial,Helvetica,sans-serif;margin:24px;color:#111">
      <h2 style="text-align:center;margin:0 0 4px">Histórico do dia</h2>
      <div style="text-align:center;color:#555;margin-bottom:16px">${dateStr} · ${history.length} ${history.length === 1 ? 'partida' : 'partidas'}</div>
      ${history.map(matchHtml).join('')}
    </body></html>`;

  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    setTimeout(() => document.body.removeChild(iframe), 1000);
  }, 250);
};

export function History() {
  const history = useSelector((state) => state.history);

  return (
    <Box sx={{ bgcolor: '#fdd8e4', minHeight: '100vh' }}>
      <TopBar />
      <Box
        sx={{
          maxWidth: 720,
          mx: 'auto',
          mt: 4,
          px: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography variant="h5" textAlign="center" mb={1}>
          Histórico do dia
        </Typography>
        <Typography variant="body2" textAlign="center" sx={{ color: 'text.secondary' }} mb={2}>
          {history.length}
          {history.length === 1 ? ' partida' : ' partidas'}
        </Typography>
        {history.length > 0 && (
          <Button
            variant="contained"
            startIcon={<PictureAsPdfIcon />}
            onClick={() => exportHistoryPdf(history)}
            sx={{ mb: 3 }}
          >
            Exportar PDF
          </Button>
        )}
        <Box sx={{
          width: '100%', display: 'flex', flexDirection: 'column', gap: 2,
        }}
        >
          {history.length > 0
            ? history.map((match) => (
              <MatchCard key={match.playedAt} match={match} />
            ))
            : (
              <>
                <Divider />
                <Typography textAlign="center" sx={{ color: 'text.secondary', mt: 2 }}>
                  Nenhuma partida registrada ainda.
                </Typography>
              </>
            )}
        </Box>
      </Box>
    </Box>
  );
}
